// SCR-STAFF-206 — پکیج‌ها و عضویت‌ها (US-CAT-050 تا 053؛ AP-CAT-03): فهرست پکیج‌ها، تعریف با اقلام بر حسب نوع (جلسات مشخص،
// انعطاف‌پذیر، اعتبار ریالی) و سیاست، نسخه‌ها با ارزش هر جلسه (پیش‌نمایش پیش‌نویس و ثبت در انتشار — AC-CAT-050.2، 050.3) و طرح‌های عضویت.
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type MembershipPlanView,
  type PackageItem,
  type PackageSummaryView,
  type PackageVersionView,
  type PackageView,
  type SessionValueLine,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  useMoneyFormatter,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { catKeys, fromBasisPoints, toBasisPoints, useActiveServices } from './common';

export const SCR_STAFF_206 = 'SCR-STAFF-206';

const PACKAGE_TYPES = ['FixedSessions', 'FlexibleSessions', 'MonetaryCredit'] as const;
const PERIODS = ['Monthly', 'Quarterly', 'Yearly'] as const;

const STATUS_TONE: Record<string, StatusTone> = {
  Draft: 'pending',
  Published: 'active',
  Superseded: 'inactive',
  Retired: 'cancelled',
};

const percent = z.string().refine((value) => value === '' || (Number(value) >= 0 && Number(value) <= 100), {
  message: 'errors:validation.invalid',
});

const packageSchema = z
  .object({
    code: z.string().trim().min(1, 'common:validation.required').max(32),
    name: z.string().trim().min(1, 'common:validation.required').max(200),
    type: z.enum(PACKAGE_TYPES),
    price: z.number({ message: 'common:validation.required' }).int().min(0),
    creditAmount: z.number().int().nullable().optional(),
    validityDays: z.string().refine((value) => Number(value) >= 1 && Number(value) <= 3650, {
      message: 'errors:validation.invalid',
    }),
    items: z.array(z.object({ services: z.array(z.string()), sessions: z.string() })).min(1),
    transferable: z.boolean(),
    cancellable: z.boolean(),
    penalty: percent,
    maxExtensions: z.string(),
    extensionPercent: percent,
  })
  .superRefine((value, ctx) => {
    value.items.forEach((item, index) => {
      if (item.services.length === 0 || (value.type === 'FixedSessions' && item.services.length !== 1)) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'services'],
          message: 'common:validation.required',
        });
      }
      if (value.type !== 'MonetaryCredit' && !(Number(item.sessions) >= 1 && Number(item.sessions) <= 100)) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'sessions'],
          message: 'errors:validation.invalid',
        });
      }
    });
    if (value.type === 'MonetaryCredit' && !(Number(value.creditAmount) > 0)) {
      ctx.addIssue({ code: 'custom', path: ['creditAmount'], message: 'common:validation.required' });
    }
  });
type PackageForm = z.input<typeof packageSchema>;

function toRequest(form: PackageForm, rowVersion?: number | string) {
  const items: PackageItem[] = form.items.map((item) =>
    form.type === 'FixedSessions'
      ? {
          itemId: '00000000-0000-0000-0000-000000000000',
          serviceId: item.services[0] ?? null,
          allowedServiceIds: [],
          allowedCategoryIds: [],
          sessions: Number(item.sessions),
        }
      : {
          itemId: '00000000-0000-0000-0000-000000000000',
          serviceId: null,
          allowedServiceIds: item.services,
          allowedCategoryIds: [],
          sessions: form.type === 'MonetaryCredit' ? 0 : Number(item.sessions),
        },
  );
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    type: form.type,
    price: { amount: form.price, currency: 'IRR' },
    creditAmount:
      form.type === 'MonetaryCredit' ? { amount: Number(form.creditAmount), currency: 'IRR' } : null,
    validityDays: Number(form.validityDays),
    branchIds: [],
    providerIds: [],
    saleChannels: ['Staff' as const],
    saleFrom: null,
    saleTo: null,
    items,
    policy: {
      transferable: form.transferable,
      maxExtensions: Number(form.maxExtensions || 0),
      extensionMaxBasisPoints: toBasisPoints(Number(form.extensionPercent || 0)),
      extensionFee: { amount: 0, currency: 'IRR' },
      cancellable: form.cancellable,
      cancellationPenaltyBasisPoints: toBasisPoints(Number(form.penalty || 0)),
      refundBasis: 'RegularPriceOfUsedSessions' as const,
      convertible: false,
    },
    rowVersion: rowVersion === undefined ? null : Number(rowVersion),
  };
}

function formOf(view: PackageView | null): PackageForm {
  const latest = view?.versions[0]?.definition;
  if (!view || !latest) {
    return {
      code: '',
      name: '',
      type: 'FixedSessions',
      price: undefined as unknown as number,
      creditAmount: null,
      validityDays: '365',
      items: [{ services: [], sessions: '6' }],
      transferable: false,
      cancellable: true,
      penalty: '10',
      maxExtensions: '1',
      extensionPercent: '50',
    };
  }
  return {
    code: view.code,
    name: latest.name,
    type: latest.type,
    price: Number(latest.price.amount),
    creditAmount: latest.creditAmount ? Number(latest.creditAmount.amount) : null,
    validityDays: String(latest.validityDays),
    items: latest.items.map((item) => ({
      services: item.serviceId ? [item.serviceId] : [...item.allowedServiceIds],
      sessions: String(item.sessions),
    })),
    transferable: latest.policy.transferable,
    cancellable: latest.policy.cancellable,
    penalty: String(fromBasisPoints(latest.policy.cancellationPenaltyBasisPoints) ?? 0),
    maxExtensions: String(latest.policy.maxExtensions),
    extensionPercent: String(fromBasisPoints(latest.policy.extensionMaxBasisPoints) ?? 0),
  };
}

function PackageDialog({
  view,
  onClose,
}: {
  view: PackageView | null;
  onClose: (saved?: PackageView) => void;
}) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const services = useActiveServices();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<PackageForm>({
    resolver: zodResolver(packageSchema),
    defaultValues: formOf(view),
  });
  const items = useFieldArray({ control, name: 'items' });
  const type = useWatch({ control, name: 'type' });

  const save = useMutation({
    mutationFn: (form: PackageForm) =>
      unwrap(
        view
          ? tenantApi.PUT('/api/v1/catalog/packages/{id}', {
              params: { path: { id: view.id } },
              body: toRequest(form, view.rowVersion),
            })
          : tenantApi.POST('/api/v1/catalog/packages', {
              body: toRequest(form),
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose(saved);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'price', 'validityDays'],
        codeFields: { 'PHX-CAT-0601': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="md"
      screenId="SCR-STAFF-206-PACKAGE"
      titleKey={view ? 'cat:packages.edit_title' : 'cat:packages.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="cat:packages.form.code"
          required
          disabled={Boolean(view)}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'package-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="cat:packages.form.name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'package-name' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="type"
          labelKey="cat:packages.form.type"
          options={PACKAGE_TYPES.map((value) => ({ value, labelKey: `cat:packages.type.${value}` }))}
        />
        <MoneyFieldRHF control={control} name="price" labelKey="cat:packages.form.price" required />
        {type === 'MonetaryCredit' && (
          <MoneyFieldRHF control={control} name="creditAmount" labelKey="cat:packages.form.credit" required />
        )}
        <TextFieldRHF
          control={control}
          name="validityDays"
          labelKey="cat:packages.form.validity_days"
          type="number"
          required
          slotProps={{ htmlInput: { min: 1, max: 3650, 'data-testid': 'package-validity' } }}
        />
      </Stack>
      <Typography variant="subtitle2">{t('cat:packages.form.items')}</Typography>
      {items.fields.map((field, index) => (
        <Stack key={field.id} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <Controller
            control={control}
            name={`items.${index}.services`}
            render={({ field: services$, fieldState }) => (
              <Autocomplete
                multiple={type !== 'FixedSessions'}
                fullWidth
                options={services.options.map((option) => option.id)}
                value={type === 'FixedSessions' ? (services$.value[0] ?? null) : services$.value}
                onChange={(_, value) =>
                  services$.onChange(Array.isArray(value) ? value : value ? [value] : [])
                }
                getOptionLabel={(id) => services.name(id)}
                filterOptions={(options) => options}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    services.search(value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      type === 'FixedSessions'
                        ? 'cat:packages.form.service'
                        : 'cat:packages.form.allowed_services',
                    )}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    slotProps={{
                      htmlInput: { ...params.inputProps, 'data-testid': `package-item-service-${index}` },
                    }}
                  />
                )}
              />
            )}
          />
          {type !== 'MonetaryCredit' && (
            <TextFieldRHF
              control={control}
              name={`items.${index}.sessions`}
              labelKey="cat:packages.form.sessions"
              type="number"
              required
              slotProps={{ htmlInput: { min: 1, max: 100, 'data-testid': `package-item-sessions-${index}` } }}
            />
          )}
          <IconButton
            aria-label={t('cat:packages.form.remove_item')}
            disabled={items.fields.length === 1}
            onClick={() => items.remove(index)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}
      <Button onClick={() => items.append({ services: [], sessions: '1' })} data-testid="package-add-item">
        {t('cat:packages.form.add_item')}
      </Button>
      <Typography variant="subtitle2">{t('cat:packages.form.policy')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('cat:packages.form.policy_help')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <CheckboxRHF control={control} name="transferable" labelKey="cat:packages.form.transferable" />
        <CheckboxRHF control={control} name="cancellable" labelKey="cat:packages.form.cancellable" />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="penalty"
          labelKey="cat:packages.form.penalty_percent"
          type="number"
        />
        <TextFieldRHF
          control={control}
          name="maxExtensions"
          labelKey="cat:packages.form.max_extensions"
          type="number"
        />
        <TextFieldRHF
          control={control}
          name="extensionPercent"
          labelKey="cat:packages.form.extension_percent"
          type="number"
        />
      </Stack>
    </FormDialog>
  );
}

function AllocationTable({ lines }: { lines: readonly SessionValueLine[] }) {
  const services = useActiveServices();
  const columns: DataTableColumn<SessionValueLine>[] = [
    {
      id: 'service',
      headerKey: 'cat:packages.allocation_columns.service',
      cell: (row) => services.name(row.serviceId),
    },
    {
      id: 'sessions',
      headerKey: 'cat:packages.allocation_columns.sessions',
      cell: (row) => faDigits(row.sessions),
    },
    {
      id: 'regular',
      headerKey: 'cat:packages.allocation_columns.regular',
      cell: (row) => (
        <Money amountMinor={row.regularUnitPrice.amount} currency={row.regularUnitPrice.currency} />
      ),
    },
    {
      id: 'session',
      headerKey: 'cat:packages.allocation_columns.session_value',
      cell: (row) => (
        <span data-testid={`allocation-session-${row.itemId}`}>
          <Money
            amountMinor={(row.sessionValues[0] as { amount?: number | string } | undefined)?.amount ?? 0}
          />
        </span>
      ),
    },
    {
      id: 'total',
      headerKey: 'cat:packages.allocation_columns.total',
      cell: (row) => <Money amountMinor={row.allocatedTotal.amount} currency={row.allocatedTotal.currency} />,
    },
  ];
  return (
    <DataTable
      captionKey="cat:packages.allocation_title"
      columns={columns}
      rows={lines}
      getRowId={(row) => row.itemId}
      emptyTitleKey="cat:packages.credit_note"
    />
  );
}

function VersionCard({ version }: { version: PackageVersionView }) {
  const { t } = useTranslation('cat');
  const money = useMoneyFormatter();
  const definition = version.definition;
  const allocation = definition.allocation ?? version.allocationPreview;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`package-version-${definition.version}`}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">
            {t('cat:packages.version_label', { version: faDigits(definition.version) })}
          </Typography>
          <StatusChip
            tone={STATUS_TONE[definition.status] ?? 'pending'}
            labelKey={`cat:packages.status.${definition.status}`}
          />
          <Money amountMinor={definition.price.amount} currency={definition.price.currency} />
        </Stack>
        {definition.status === 'Draft' && (
          <Typography variant="caption" color="text.secondary">
            {t('cat:packages.allocation_preview')}
          </Typography>
        )}
        {version.allocationError && (
          <Typography color="error" role="alert">
            {t(
              `errors:error.${version.allocationError === 'PHX-CAT-0604' ? 'package_regular_price_missing' : 'unexpected'}`,
            )}
          </Typography>
        )}
        {allocation && (
          <>
            <AllocationTable lines={allocation.lines} />
            <Typography variant="body2" data-testid={`allocation-total-${definition.version}`}>
              {t('cat:packages.allocation_total', {
                total: money(
                  allocation.lines.reduce((sum, line) => sum + Number(line.allocatedTotal.amount), 0),
                ),
                regular: money(allocation.regularTotal.amount),
              })}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function PackageDetails({ id, onEdit }: { id: string; onEdit: (view: PackageView) => void }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.package.manage');
  const [confirm, setConfirm] = useState<'publish' | 'retire' | null>(null);
  const detail = useQuery({
    queryKey: catKeys.package(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/packages/{id}', { params: { path: { id } } })),
  });
  const action = useMutation({
    mutationFn: (kind: 'publish' | 'retire') =>
      unwrap(
        kind === 'publish'
          ? tenantApi.POST('/api/v1/catalog/packages/{id}:publish', { params: { path: { id } } })
          : tenantApi.POST('/api/v1/catalog/packages/{id}:retire', { params: { path: { id } } }),
      ),
    onSuccess: () => {
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
  });
  const view = detail.data;
  if (!view) {
    return null;
  }
  const hasDraft = view.versions.some((version) => version.definition.status === 'Draft');
  const published = view.versions.some((version) => version.definition.status === 'Published');

  return (
    <Stack spacing={2} data-testid="package-details">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" component="h2">
          {t('cat:packages.details_title')} — {view.name}
        </Typography>
        {write.canWrite && view.status !== 'Retired' && (
          <Button onClick={() => onEdit(view)} data-testid="package-edit">
            {t('cat:packages.edit')}
          </Button>
        )}
        {write.canWrite && hasDraft && view.status !== 'Retired' && (
          <Button variant="contained" onClick={() => setConfirm('publish')} data-testid="package-publish">
            {t('cat:packages.publish')}
          </Button>
        )}
        {write.canWrite && published && (
          <Button color="warning" onClick={() => setConfirm('retire')} data-testid="package-retire">
            {t('cat:packages.retire')}
          </Button>
        )}
      </Stack>
      {view.versions.map((version) => (
        <VersionCard key={version.definition.versionId} version={version} />
      ))}
      <ConfirmDialog
        open={confirm !== null}
        titleKey={
          confirm === 'retire' ? 'cat:packages.confirm_retire_title' : 'cat:packages.confirm_publish_title'
        }
        messageKey={
          confirm === 'retire'
            ? 'cat:packages.confirm_retire_message'
            : 'cat:packages.confirm_publish_message'
        }
        confirmKey={confirm === 'retire' ? 'cat:packages.retire' : 'cat:packages.publish'}
        destructive={confirm === 'retire'}
        busy={action.isPending}
        error={action.error}
        onConfirm={() => confirm && action.mutate(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </Stack>
  );
}

function PackagesTab() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('cat.package.manage');
  const [selected, setSelected] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ view: PackageView | null } | null>(null);
  const list = useQuery({
    queryKey: catKeys.packages(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/packages')),
  });

  const columns: DataTableColumn<PackageSummaryView>[] = [
    { id: 'code', headerKey: 'cat:packages.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'name', headerKey: 'cat:packages.columns.name', cell: (row) => row.name },
    { id: 'type', headerKey: 'cat:packages.columns.type', cell: (row) => t(`cat:packages.type.${row.type}`) },
    {
      id: 'price',
      headerKey: 'cat:packages.columns.price',
      cell: (row) => <Money amountMinor={row.price.amount} currency={row.price.currency} />,
    },
    {
      id: 'validity',
      headerKey: 'cat:packages.columns.validity',
      cell: (row) => t('cat:packages.validity_days', { days: faDigits(row.validityDays) }),
    },
    {
      id: 'status',
      headerKey: 'cat:packages.columns.status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <StatusChip
            tone={STATUS_TONE[row.status] ?? 'pending'}
            labelKey={`cat:packages.status.${row.status}`}
          />
          {row.hasDraft && row.status !== 'Draft' && (
            <StatusChip tone="pending" labelKey="cat:packages.has_draft" />
          )}
        </Stack>
      ),
    },
    {
      id: 'version',
      headerKey: 'cat:packages.columns.version',
      cell: (row) => (row.publishedVersion ? faDigits(row.publishedVersion) : '—'),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      {write.canWrite && (
        <Stack direction="row">
          <Button variant="contained" onClick={() => setDialog({ view: null })} data-testid="package-create">
            {t('cat:packages.create')}
          </Button>
        </Stack>
      )}
      <DataTable
        captionKey="cat:packages.caption"
        columns={columns}
        rows={list.data?.items}
        getRowId={(row) => row.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitleKey="cat:packages.empty"
        onRowClick={(row) => setSelected(row.id)}
      />
      {selected ? (
        <PackageDetails id={selected} onEdit={(view) => setDialog({ view })} />
      ) : (
        <Typography color="text.secondary">{t('cat:packages.select_hint')}</Typography>
      )}
      {dialog && (
        <PackageDialog
          view={dialog.view}
          onClose={(saved) => {
            setDialog(null);
            if (saved) {
              setSelected(saved.id);
            }
          }}
        />
      )}
    </Stack>
  );
}

const membershipSchema = z.object({
  code: z.string().trim().min(1, 'common:validation.required').max(32),
  name: z.string().trim().min(1, 'common:validation.required').max(200),
  period: z.enum(PERIODS),
  price: z.number({ message: 'common:validation.required' }).int().min(0),
  discount: percent,
  carryOver: z.boolean(),
  priorityBooking: z.boolean(),
});
type MembershipForm = z.input<typeof membershipSchema>;

function MembershipDialog({ plan, onClose }: { plan: MembershipPlanView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const definition = plan?.definition;
  const { control, handleSubmit, formState, setError } = useForm<MembershipForm>({
    resolver: zodResolver(membershipSchema),
    defaultValues: {
      code: definition?.code ?? '',
      name: definition?.name ?? '',
      period: definition?.period ?? 'Monthly',
      price: definition ? Number(definition.price.amount) : (undefined as unknown as number),
      discount: String(fromBasisPoints(definition?.discountBasisPoints) ?? 0),
      carryOver: definition?.carryOver ?? false,
      priorityBooking: definition?.priorityBooking ?? false,
    },
  });
  const save = useMutation({
    mutationFn: (form: MembershipForm) => {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        period: form.period,
        price: { amount: form.price, currency: 'IRR' },
        discountBasisPoints: toBasisPoints(Number(form.discount || 0)),
        discountCategoryIds: [...(definition?.discountCategoryIds ?? [])],
        freeSessions: [...(definition?.freeSessions ?? [])],
        carryOver: form.carryOver,
        priorityBooking: form.priorityBooking,
        rowVersion: plan ? Number(plan.rowVersion) : null,
      };
      return unwrap(
        plan
          ? tenantApi.PUT('/api/v1/catalog/membership-plans/{id}', {
              params: { path: { id: plan.definition.planId } },
              body,
            })
          : tenantApi.POST('/api/v1/catalog/membership-plans', {
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name'],
        codeFields: { 'PHX-CAT-0601': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-206-MEMBERSHIP"
      titleKey={plan ? 'cat:memberships.edit_title' : 'cat:memberships.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="cat:memberships.form.code"
          required
          disabled={Boolean(plan)}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'membership-code' } }}
        />
        <TextFieldRHF control={control} name="name" labelKey="cat:memberships.form.name" required fullWidth />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="period"
          labelKey="cat:memberships.form.period"
          options={PERIODS.map((value) => ({ value, labelKey: `cat:memberships.period.${value}` }))}
        />
        <MoneyFieldRHF control={control} name="price" labelKey="cat:memberships.form.price" required />
        <TextFieldRHF
          control={control}
          name="discount"
          labelKey="cat:memberships.form.discount_percent"
          type="number"
        />
      </Stack>
      <CheckboxRHF control={control} name="carryOver" labelKey="cat:memberships.form.carry_over" />
      <CheckboxRHF control={control} name="priorityBooking" labelKey="cat:memberships.form.priority_booking" />
    </FormDialog>
  );
}

function MembershipsTab() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.package.manage');
  const [dialog, setDialog] = useState<{ plan: MembershipPlanView | null } | null>(null);
  const list = useQuery({
    queryKey: catKeys.memberships(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/membership-plans')),
  });
  const action = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'publish' | 'retire' }) =>
      unwrap(
        kind === 'publish'
          ? tenantApi.POST('/api/v1/catalog/membership-plans/{id}:publish', { params: { path: { id } } })
          : tenantApi.POST('/api/v1/catalog/membership-plans/{id}:retire', { params: { path: { id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) }),
  });

  const columns: DataTableColumn<MembershipPlanView>[] = [
    {
      id: 'code',
      headerKey: 'cat:memberships.columns.code',
      cell: (row) => <bdi dir="ltr">{row.definition.code}</bdi>,
    },
    { id: 'name', headerKey: 'cat:memberships.columns.name', cell: (row) => row.definition.name },
    {
      id: 'period',
      headerKey: 'cat:memberships.columns.period',
      cell: (row) => t(`cat:memberships.period.${row.definition.period}`),
    },
    {
      id: 'price',
      headerKey: 'cat:memberships.columns.price',
      cell: (row) => (
        <Money amountMinor={row.definition.price.amount} currency={row.definition.price.currency} />
      ),
    },
    {
      id: 'discount',
      headerKey: 'cat:memberships.columns.discount',
      cell: (row) =>
        t('cat:discounts.value_percent', {
          value: faDigits(fromBasisPoints(row.definition.discountBasisPoints) ?? 0),
        }),
    },
    {
      id: 'status',
      headerKey: 'cat:memberships.columns.status',
      cell: (row) => (
        <StatusChip
          tone={STATUS_TONE[row.definition.status] ?? 'pending'}
          labelKey={`cat:memberships.status.${row.definition.status}`}
        />
      ),
    },
    {
      id: 'version',
      headerKey: 'cat:memberships.columns.version',
      cell: (row) => faDigits(row.definition.version),
    },
    {
      id: 'actions',
      headerKey: 'cat:memberships.columns.actions',
      cell: (row) =>
        write.canWrite ? (
          <Stack direction="row" spacing={1}>
            {row.definition.status !== 'Retired' && (
              <Button size="small" onClick={() => setDialog({ plan: row })}>
                {t('cat:memberships.edit')}
              </Button>
            )}
            {row.definition.status === 'Draft' && (
              <Button
                size="small"
                onClick={() => action.mutate({ id: row.definition.planId, kind: 'publish' })}
              >
                {t('cat:memberships.publish')}
              </Button>
            )}
            {row.definition.status === 'Published' && (
              <Button
                size="small"
                color="warning"
                onClick={() => action.mutate({ id: row.definition.planId, kind: 'retire' })}
              >
                {t('cat:memberships.retire')}
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];

  return (
    <Stack spacing={2}>
      {write.canWrite && (
        <Stack direction="row">
          <Button
            variant="contained"
            onClick={() => setDialog({ plan: null })}
            data-testid="membership-create"
          >
            {t('cat:memberships.create')}
          </Button>
        </Stack>
      )}
      <DataTable
        captionKey="cat:memberships.caption"
        columns={columns}
        rows={list.data?.items}
        getRowId={(row) => row.definition.planId}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitleKey="cat:memberships.empty"
      />
      {dialog && <MembershipDialog plan={dialog.plan} onClose={() => setDialog(null)} />}
    </Stack>
  );
}

export function PackagesPage() {
  const { t } = useTranslation('cat');
  const [tab, setTab] = useState<'packages' | 'memberships'>('packages');
  return (
    <ListPage screenId={SCR_STAFF_206} titleKey="cat:packages.title" subtitleKey="cat:packages.subtitle">
      <Tabs value={tab} onChange={(_, value: 'packages' | 'memberships') => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="packages" label={t('cat:packages.tabs.packages')} />
        <Tab value="memberships" label={t('cat:packages.tabs.memberships')} data-testid="tab-memberships" />
      </Tabs>
      {tab === 'packages' ? <PackagesTab /> : <MembershipsTab />}
    </ListPage>
  );
}
